import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { MailQuestion } from 'lucide-react';
import { useAuth } from '../contextos/AuthContext';
import { aceitarConvite, cadastrarComConvite } from '../servicos/primeiroAcesso';
import { Erro } from '../componentes/Estados';
import { CampoSenha } from '../componentes/Campos';

/**
 * Cadastro de quem foi convidado.
 *
 * Isto NÃO é cadastro público: criar conta no Firebase Auth é possível para
 * qualquer um (a configuração é pública). O que dá acesso é o TOKEN que veio no
 * link do convite — e ele só existe dentro do e-mail enviado ao endereço
 * convidado. Quem confere é a Cloud Function, não esta tela.
 *
 * Sem token na URL nem adianta mostrar o formulário: a conta seria criada e
 * ficaria órfã, e a pessoa levaria a culpa por um passo que ela não errou.
 */
const MENSAGENS: Record<string, string> = {
  'auth/email-already-in-use':
    'Já existe conta com este e-mail. Entre normalmente — se o acesso não abrir, volte a este link do convite.',
  'auth/weak-password': 'A senha precisa ter pelo menos 6 caracteres.',
  'auth/invalid-email': 'E-mail inválido.',
  'auth/network-request-failed': 'Sem conexão. Verifique a internet e tente de novo.',
};

export function PrimeiroAcesso() {
  const { estado, reavaliar } = useAuth();
  const [parametros] = useSearchParams();
  const token = parametros.get('convite') ?? '';

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (estado === 'LIBERADO') return <Navigate to="/" replace />;
  if (!token) return <SemToken />;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await cadastrarComConvite(email, senha, nome, token);
      await reavaliar();
    } catch (ex) {
      const codigo = (ex as { code?: string }).code ?? '';
      // A função devolve o motivo já escrito para ser lido (convite expirado,
      // link inválido, já usado). Mostrar esse texto é melhor que traduzir um
      // código genérico e perder a única informação útil.
      const daFuncao = (ex as { message?: string }).message;
      setErro(
        MENSAGENS[codigo] ??
          (daFuncao && !/^internal$/i.test(daFuncao)
            ? daFuncao
            : 'Não foi possível criar a conta. Tente de novo.'),
      );
      setEnviando(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-papel p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-semibold tracking-tight">
            Profit<span className="text-marca">Lens</span>
          </h1>
          <p className="mt-1 text-sm text-tinta-2">Primeiro acesso</p>
        </div>

        <form
          onSubmit={enviar}
          className="flex flex-col gap-4 rounded-md border border-regua-2 bg-superficie p-6"
        >
          <p className="rounded border-l-3 border-marca bg-marca/10 px-3 py-2 text-xs text-tinta-2">
            Use <strong>exatamente o e-mail que recebeu o convite</strong>. É por ele que o sistema
            reconhece seu acesso e define seu perfil.
          </p>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Nome</span>
            <input
              type="text"
              required
              autoComplete="name"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="rounded border border-regua-2 bg-papel px-3 py-2 text-sm outline-none focus:border-marca"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">E-mail do convite</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-regua-2 bg-papel px-3 py-2 text-sm outline-none focus:border-marca"
            />
          </label>

          <CampoSenha
            rotulo="Senha"
            valor={senha}
            aoMudar={setSenha}
            autoComplete="new-password"
            minimo={6}
            dica="Pelo menos 6 caracteres."
          />

          {erro && <Erro titulo={erro} />}

          <button
            type="submit"
            disabled={enviando}
            className="rounded bg-marca px-4 py-2 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
          >
            {enviando ? 'Criando conta…' : 'Criar conta'}
          </button>
        </form>

        <JaTenhoConta token={token} />

        <p className="mt-4 text-center text-xs text-tinta-3">
          Já tem conta?{' '}
          <Link to="/entrar" className="text-marca underline-offset-2 hover:underline">
            Entrar
          </Link>
        </p>
      </div>
    </main>
  );
}

/**
 * Quem criou a conta e parou antes de aceitar fica autenticado e sem acesso.
 * Voltar ao link resolve — mas só se a tela oferecer o caminho, em vez de
 * repetir um formulário de cadastro que agora só daria "e-mail já em uso".
 */
function JaTenhoConta({ token }: { token: string }) {
  const { estado, reavaliar } = useAuth();
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  if (estado !== 'SEM_PERFIL') return null;

  return (
    <div className="mt-4 rounded-md border border-regua-2 bg-superficie p-4 text-center">
      <p className="text-xs text-tinta-2">
        Você já está autenticado, mas sem acesso liberado. Use este convite na conta atual:
      </p>
      <button
        type="button"
        disabled={ocupado}
        onClick={async () => {
          setErro(null);
          setOcupado(true);
          try {
            await aceitarConvite(token);
            await reavaliar();
          } catch (e) {
            setErro((e as { message?: string }).message ?? 'Não deu para usar este convite.');
            setOcupado(false);
          }
        }}
        className="mt-2 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
      >
        {ocupado ? 'Liberando…' : 'Usar o convite nesta conta'}
      </button>
      {erro && <p className="mt-2 text-xs text-pend">{erro}</p>}
    </div>
  );
}

function SemToken() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-papel p-6">
      <div className="flex max-w-md flex-col items-center gap-4 rounded-md border border-regua-2 bg-superficie p-8 text-center">
        <MailQuestion className="size-8 text-tinta-3" aria-hidden />
        <h1 className="text-xl font-semibold">Abra o link do convite</h1>
        <p className="text-sm text-tinta-2">
          O ProfitLens é privado e funciona por convite. O acesso vem do link que chega por e-mail —
          criar uma conta por aqui, sem ele, não libera nada.
        </p>
        <p className="text-sm text-tinta-2">
          Não recebeu? Confira a caixa de spam ou peça a um administrador para enviar de novo.
        </p>
        <Link to="/entrar" className="text-sm text-marca underline-offset-2 hover:underline">
          Já tenho conta — entrar
        </Link>
      </div>
    </main>
  );
}
